import { Injectable } from "@angular/core";
import { DexieDbProvider } from "@shared/services/dexie-provider";
import { Guid } from "guid-typescript";

const IMAGE_FETCH_TIMEOUT_MS = 15000;

export class BlobImage {
    constructor(url: string, data: Blob, mimeType?: string) {
        this.url = removeUrlParameters(url);
        this.data = data;
        this.mimeType = mimeType;
        this.id = stripExtension(this.url.split('/').pop()!);
    }

    id: string;
    url: string;
    data: Blob;
    mimeType?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ImageService {

    constructor(private localDbProvider: DexieDbProvider) { }

    async getImageUrl(url?: string) {

        if(!url) return url;

        let image: BlobImage = await this.readImageByUrl(url);
        if (image) {
            return URL.createObjectURL(image.data);
        }

        this.fetchImage(url)
            .then(blob => this.saveImageToDatabase(url, blob));

        return url;
    }

    async fetchImage(url: string): Promise<Blob> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS);
        try {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) {
                throw new Error(`Image fetch failed (${response.status}): ${url}`);
            }
            const blob = await response.blob();
            if (!blob.type.startsWith('image/')) {
                throw new Error(`Unexpected blob type "${blob.type}" for ${url}`);
            }
            return blob;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    async saveImageToDatabase(url: string, blob: Blob) {
        this.saveImage(new BlobImage(url, blob, blob.type));
    }

    saveImage(image: BlobImage): Promise<string> {
        return this.localDbProvider.db.BlobImages.put(image);
    }

    readImageByUrl(url: string): Promise<BlobImage> {
        url = removeUrlParameters(url);
        return this.localDbProvider.db.BlobImages
            .where("url")
            .equals(url)
            .first();
    }

    // Stores a blob keyed by a stable file name (the persistent identifier on stock items)
    async saveImageBlobByFileName(fileName: string, blob: Blob): Promise<string> {
        return this.localDbProvider.db.BlobImages.put({
            id: stripExtension(fileName),
            url: fileName,
            data: blob,
            mimeType: blob.type
        });
    }

    async getImageObjectUrlByFileName(fileName?: string): Promise<string | undefined> {
        if (!fileName) return undefined;
        const id = stripExtension(fileName);
        if (!id) return undefined;
        const image = await this.localDbProvider.db.BlobImages.get(id);
        return image ? URL.createObjectURL(image.data) : undefined;
    }

}

export function removeUrlParameters(url: string) {
    try{
      const urlObj = new URL(url);
      urlObj.search = '';
      urlObj.hash = '';

      return urlObj.toString();
    } catch {
      return url;
    }
}

export function getFileName(url: string | undefined, imageExtention: string) {
    if (!url) {
        return `${Guid.create().toString()}.${imageExtention}`;
    }

    const urlObj = removeUrlParameters(url);
    const id = stripExtension(urlObj.split('/').pop()!);

    return `${id}.${imageExtention}`;
}

// Strips the last extension only — preserves dots inside the base name
// (e.g. "asset.v2.png" → "asset.v2" so we don't collide with "asset.png").
export function stripExtension(fileName: string): string {
    return fileName.replace(/\.[^.\/]+$/, '');
}
